import {
  EntitiesChanges,
  EntityChangeType,
  LogDisplay,
  LogType,
} from '@rolebase/shared/model/log'
import isEqual from 'lodash.isequal'
import { sameOnScreen } from './logDisplayText'

// The fields of a role, in the order a reader expects them (the role's own)
export const LOG_FIELDS_ORDER = [
  'name',
  'purpose',
  'domain',
  'accountabilities',
  'checklist',
  'indicators',
  'notes',
  'singleMember',
  'autoCreate',
  'base',
  'parentLink',
  'colorHue',
  'parentId',
  'roleId',
  'memberId',
  'circleId',
  'avgMinPerWeek',
  'archived',
  'archivedAt',
] as const

// Identifiers carry no meaning for a reader
const HIDDEN_FIELDS = ['id', 'orgId']

// The entries whose detail is worth opening (#114, D5): a new or changed role
// and a moved circle. « X joined the role » already says everything.
const DETAIL_TYPES: LogType[] = [
  LogType.RoleUpdate,
  LogType.RoleCreate,
  LogType.CircleCreate,
  LogType.CircleMove,
]

export type LogChangeSummary =
  // One role changed: these fields, in the role's order
  | { kind: 'fields'; fields: string[] }
  // Saved, but nothing a reader can see changed
  | { kind: 'format' }
  // Several roles changed together (a proposal adopted)
  | { kind: 'several'; names: string[] }
  // Nothing more to say than the usual sentence
  | { kind: 'none' }

interface LogLike {
  display: LogDisplay
  changes: EntitiesChanges
}

/**
 * What the news card says of a change to the org chart (#114): which fields of
 * the role changed, or that only its formatting did, or which roles changed.
 */
export function logChangeSummary(
  log: LogLike,
  getRoleName?: (id: string) => string | undefined
): LogChangeSummary {
  if (log.display.type !== LogType.RoleUpdate) return { kind: 'none' }

  const updates = (log.changes.roles ?? []).flatMap((change) =>
    change.type === EntityChangeType.Update ? [change] : []
  )
  if (updates.length === 0) return { kind: 'none' }

  if (updates.length > 1) {
    return {
      kind: 'several',
      names: updates.map(
        (change) =>
          change.newData.name ??
          change.prevData.name ??
          getRoleName?.(change.id) ??
          '…'
      ),
    }
  }

  const { prevData, newData } = updates[0]
  const keys = [
    ...new Set([...Object.keys(prevData), ...Object.keys(newData)]),
  ].filter((key) => !HIDDEN_FIELDS.includes(key))
  const read = (data: object, key: string) =>
    (data as Record<string, unknown>)[key]

  const fields = keys
    .filter((key) => !sameOnScreen(read(prevData, key), read(newData, key)))
    .sort((a, b) => fieldRank(a) - fieldRank(b))
  if (fields.length > 0) return { kind: 'fields', fields }

  const anyChange = keys.some(
    (key) => !isEqual(read(prevData, key), read(newData, key))
  )
  return anyChange ? { kind: 'format' } : { kind: 'none' }
}

// Whether the news card opens a window with the detail of the change
export function logHasDetails(log: LogLike): boolean {
  if (!DETAIL_TYPES.includes(log.display.type)) return false
  const hasChanges = Object.values(log.changes).some(
    (changes) => changes && changes.length > 0
  )
  // A role saved with nothing a reader can see changed has nothing to show
  const summary = logChangeSummary(log)
  return (
    hasChanges &&
    summary.kind !== 'format' &&
    !(log.display.type === LogType.RoleUpdate && summary.kind === 'none')
  )
}

function fieldRank(key: string): number {
  const index = (LOG_FIELDS_ORDER as readonly string[]).indexOf(key)
  return index === -1 ? LOG_FIELDS_ORDER.length : index
}
